import { useState } from "react";
import dayjs from "dayjs";
import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  CopyButton,
  Fieldset,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconLink,
  IconRefresh,
  IconUserMinus,
} from "@tabler/icons-react";

import { modals } from "@mantine/modals";

import { webConfig } from "../../../config.js";
import { useRotateClaimCode, useUnclaimTicket } from "../../api/tickets";
import { useConfirm } from "../../hooks/useConfirm";
import EmailTicketButton, { TicketEmailState } from "./EmailTicketButton";
import {
  CLAIMED_VIA_LABELS,
  formatClaimCode,
  getClaimAccount,
  isTicketClaimed,
} from "../../utils/ticketClaim";

const MASK = "•••• ••••";

/**
 * The confirmation before issuing a new ticket code, the same on the ticket
 * page and in the tickets preview.
 */
export const rotateConfirmation = (isClaimed) => ({
  title: "Issue a new ticket code?",
  message: isClaimed
    ? "The current code and ticket link stop working, also the ones in the ticket email. The ticket stays on the account that holds it."
    : "The current code and ticket link stop working, also the ones in the ticket email. Email the ticket again or share the new link; Send ticket emails includes it again.",
  confirmLabel: "Issue new code",
});

function UnclaimMessage({ account, onNewCodeChange }) {
  return (
    <Stack gap="sm">
      <Text size="sm">
        {account?.isAnonymous
          ? "It leaves this guest account, which is signed out and cannot get back in. The holder details stay."
          : "It leaves this account. The holder details stay."}{" "}
        The attendee can add it again with the same code or link, the ones in
        their ticket email.
      </Text>
      <Checkbox
        label="Also give it a new code"
        description="For a ticket the wrong person added. The old code and link stop working; email the ticket again or share the new link."
        onChange={(event) => onNewCodeChange(event.currentTarget.checked)}
      />
    </Stack>
  );
}

/**
 * The confirmation before taking a ticket off its account, the same on the
 * ticket page, in the tickets preview and in the list. It keeps the code by
 * default; `onConfirm(newCode)` says whether to also give it a new one.
 */
export const confirmUnclaim = ({ account, onConfirm }) => {
  let newCode = false;
  modals.openConfirmModal({
    title: "Take this ticket out of the app?",
    centered: true,
    children: (
      <UnclaimMessage
        account={account}
        onNewCodeChange={(checked) => {
          newCode = checked;
        }}
      />
    ),
    labels: { confirm: "Take out", cancel: "Cancel" },
    confirmProps: { color: "red" },
    onConfirm: () => onConfirm(newCode),
  });
};

/** Body for the take-out mutation: the code stays unless asked. */
export const unclaimBody = (newCode) => (newCode ? { newCode: true } : {});

/**
 * How a ticket nobody holds gets into the app, true to whether the ticket
 * email with its current code went out.
 */
export const unclaimedHint = (ticket) => {
  if (!ticket.email) {
    return "No account holds this ticket yet. It has no email: share the code or link with the attendee.";
  }
  if (ticket.ticketEmailSentAt) {
    return "No account holds this ticket yet. The attendee can add it in the app with the code or link in their ticket email.";
  }
  return "No account holds this ticket yet, and its current code has not been emailed. Email the ticket, or share the code or link.";
};

// Where a ticket is in the app, as one status: the most important one wins.
const APP_STATUSES = {
  notAdded: {
    label: "Not added",
    color: "gray",
    hint: "Not in the app yet.",
  },
  inApp: {
    label: "In app",
    color: "green",
    hint: "In the app, on an account that can sign in again.",
  },
  guest: {
    label: "Guest",
    color: "yellow",
    hint: "In the app without email, Google or Apple. Lost if they log out.",
  },
  lockedOut: {
    label: "Locked out",
    color: "red",
    hint: "Guest with no device left. Take the ticket out so they can add it again.",
  },
};

export function getAppStatus(ticket) {
  if (!isTicketClaimed(ticket)) return "notAdded";
  const account = getClaimAccount(ticket);
  if (account?.signedOut) return "lockedOut";
  if (account?.isAnonymous) return "guest";
  return "inApp";
}

/** The ticket's one app status, with what it means on hover. */
export function AppStatusBadge({ ticket }) {
  const status = APP_STATUSES[getAppStatus(ticket)];
  return (
    <Tooltip label={status.hint} multiline w={220} withArrow>
      <Badge color={status.color} variant="light">
        {status.label}
      </Badge>
    </Tooltip>
  );
}

/**
 * Who holds the ticket in the app, and the organizer's two levers on that: a
 * new claim code, and taking the ticket off its account. The claim code is
 * private (unlike the badge QR) so it stays masked until revealed.
 */
function ClaimStatus({ ticket }) {
  const confirm = useConfirm();
  const [isCodeVisible, setIsCodeVisible] = useState(false);
  const { mutate: rotateCode, isPending: isRotating } = useRotateClaimCode({
    ticketId: ticket.id,
  });
  const { mutate: unclaim, isPending: isUnclaiming } = useUnclaimTicket({
    ticketId: ticket.id,
  });

  const isClaimed = isTicketClaimed(ticket);
  const account = getClaimAccount(ticket);
  const claimLink = ticket.claimCode
    ? `${webConfig.baseUrl}/open/claim/${ticket.claimCode}`
    : null;

  const confirmRotate = () =>
    confirm({
      ...rotateConfirmation(isClaimed),
      onConfirm: () => rotateCode({}),
    });

  const confirmTakeOut = () =>
    confirmUnclaim({
      account,
      onConfirm: (newCode) => unclaim(unclaimBody(newCode)),
    });

  return (
    <Fieldset legend="App access" mb="md">
      <Stack gap="sm">
        <Group gap="xs">
          <AppStatusBadge ticket={ticket} />
        </Group>

        {isClaimed ? (
          <Stack gap={2}>
            <Text size="sm">
              {account?.name || "Unnamed account"}
              {account?.email ? (
                <Text span c="dimmed" size="sm">
                  {` (${account.email})`}
                </Text>
              ) : (
                <Text span c="dimmed" size="sm">
                  {" (no email)"}
                </Text>
              )}
            </Text>
            {(ticket.claimedAt || ticket.claimedVia) && (
              <Text size="xs" c="dimmed">
                {[
                  ticket.claimedAt &&
                    `Added ${dayjs(ticket.claimedAt).format("DD.MM.YYYY. HH:mm")}`,
                  ticket.claimedVia &&
                    (CLAIMED_VIA_LABELS[ticket.claimedVia] ??
                      ticket.claimedVia),
                ]
                  .filter(Boolean)
                  .join(" via ")}
              </Text>
            )}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            {unclaimedHint(ticket)}
          </Text>
        )}
        <TicketEmailState ticket={ticket} />

        {ticket.claimCode && (
          <Group gap="xs" align="center">
            <Text size="sm" fw={500}>
              Ticket code
            </Text>
            <Text
              size="sm"
              ff="monospace"
              data-testid="claim-code-value"
              style={{ letterSpacing: 2 }}
            >
              {isCodeVisible ? formatClaimCode(ticket.claimCode) : MASK}
            </Text>
            <Tooltip
              label={isCodeVisible ? "Hide code" : "Show code"}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={isCodeVisible ? "Hide code" : "Show code"}
                onClick={() => setIsCodeVisible((visible) => !visible)}
              >
                {isCodeVisible ? (
                  <IconEyeOff size={16} />
                ) : (
                  <IconEye size={16} />
                )}
              </ActionIcon>
            </Tooltip>
            <CopyButton value={ticket.claimCode} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? "Copied" : "Copy code"} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color={copied ? "green" : "gray"}
                    aria-label="Copy code"
                    onClick={copy}
                  >
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
            {claimLink && (
              <CopyButton value={claimLink} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? "Copied" : "Copy ticket link"}
                    withArrow
                  >
                    <ActionIcon
                      variant="subtle"
                      color={copied ? "green" : "gray"}
                      aria-label="Copy ticket link"
                      onClick={copy}
                    >
                      {copied ? (
                        <IconCheck size={16} />
                      ) : (
                        <IconLink size={16} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            )}
          </Group>
        )}

        <Group gap="xs">
          <EmailTicketButton ticket={ticket} variant="light" />
          <Button
            variant="light"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={confirmRotate}
            loading={isRotating}
          >
            Rotate code
          </Button>
          {isClaimed && (
            <Button
              variant="light"
              color="red"
              size="xs"
              leftSection={<IconUserMinus size={14} />}
              onClick={confirmTakeOut}
              loading={isUnclaiming}
            >
              Take out of app
            </Button>
          )}
        </Group>
      </Stack>
    </Fieldset>
  );
}

export default ClaimStatus;
